"use client";

import { useRef } from "react";

import { strings } from "@perceptron/constants";
import { Button } from "@perceptron/ui/components/button";
import { Icon } from "@perceptron/ui/components/icon";
import { toast } from "sonner";

import { TermsAndPrivacyDisclaimer } from "@demo/components/TermsAndPrivacyDisclaimer";
import { EditorDropZone } from "@demo/features/drop-zone";
import AnnotatedImageView from "@demo/features/media/AnnotatedImageView";
import { addDetectImage, resetDetectImage } from "@demo/libs/detect";
import { useDetectStore } from "@demo/store/detect";
import { useMediaCarouselStore } from "@demo/store/media-carousel";

/**
 * Detect's right pane. No image → an upload/drop zone; image present → the
 * shared `AnnotatedImageView` (it owns the annotation overlay, dim mask, and
 * zoom-to-clicked-box, reading detections from the model store). Detections are
 * written by `runDetect`.
 */
const DetectImageView = ({
  isFullscreen,
  setIsFullscreen,
}: {
  isFullscreen: boolean;
  setIsFullscreen: (val: boolean) => void;
}) => {
  const image = useDetectStore((state) => state.image);

  const isProcessing = useDetectStore((state) => state.status === "running");
  const media = useMediaCarouselStore((state) => state.mediaInAgentCarousel);
  const currentMediaIndex = useMediaCarouselStore((state) => state.currentMediaIndexInAgentCarousel);
  const currentMedia = media[currentMediaIndex];
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFiles = (files: File[]) => {
    const file = files[0];
    if (!file) return;
    addDetectImage(file).catch((error) => {
      console.error("Failed to load image:", error);
      toast.error(strings.mediaCarousel.errors.imageFailedToLoad);
    });
  };

  if (!image || !currentMedia) {
    return (
      <EditorDropZone
        className="group relative flex h-full w-full cursor-pointer items-center justify-center rounded-2xl bg-black sm:h-full"
        data-testid="detect-upload"
        onClick={() => inputRef.current?.click()}
        onFilesDropped={handleFiles}
      >
        <input
          accept="image/*"
          className="hidden"
          ref={inputRef}
          type="file"
          onChange={(e) => {
            const files = e.target.files ? Array.from(e.target.files) : [];
            if (files.length) handleFiles(files);
            e.target.value = "";
          }}
        />
        <div className="flex flex-col items-center gap-3 text-white">
          <div className="pointer-events-none relative h-[48px] w-[48px]">
            <Icon
              alt={strings.mediaSetCollection.pictureIconBackground}
              className="absolute bottom-0 left-0.5 size-11 fill-transparent transition-transform duration-300 group-hover:translate-x-[-8px] group-hover:translate-y-[-4px] group-hover:rotate-[-20deg]"
              name="album-image"
            />
            <Icon
              alt={strings.mediaSetCollection.pictureIconForeground}
              className="absolute size-12 fill-black transition-transform duration-300 group-hover:-translate-y-1 group-hover:translate-x-2 group-hover:rotate-[20deg]"
              name="album-image"
            />
          </div>
          <span className="body">{strings.detect.uploadImage}</span>
        </div>
        <TermsAndPrivacyDisclaimer className="absolute bottom-4" />
      </EditorDropZone>
    );
  }

  return (
    <div
      className="transition-[height, opacity] z-50 flex h-full items-center justify-center overflow-hidden rounded-2xl bg-black transition-[height] duration-500 ease-in-out sm:duration-300"
      data-testid="media-carousel"
    >
      <div className="relative flex h-full w-full items-center justify-center">
        {currentMedia && <AnnotatedImageView isProcessing={isProcessing} media={currentMedia} />}

        {/* Fullscreen toggle — mobile only, bottom-right. */}
        <div className="from-white/6 absolute bottom-2 right-2 rounded-[8px] border border-solid border-transparent bg-foreground bg-gradient-to-br to-black/20 bg-origin-border p-2 dark:bg-background sm:hidden">
          <Icon
            className="size-5 text-white"
            name={isFullscreen ? "minimize" : "maximize"}
            onClick={() => setIsFullscreen(!isFullscreen)}
          />
        </div>

        {/* Reset image — bottom-left on mobile (opposite the toggle), bottom-right on desktop. */}
        <Button
          aria-label={strings.detect.resetImageAriaLabel}
          className="dark absolute bottom-2 left-2 bg-background-alt-1 p-2 transition-opacity hover:opacity-80 sm:left-auto sm:right-2"
          size="icon"
          type="button"
          variant="secondary"
          onClick={() => resetDetectImage()}
        >
          <Icon className="size-6 text-white" name="trash-2" />
        </Button>
      </div>
    </div>
  );
};

export default DetectImageView;
